// The isometric farm (spec 2026-09-22). Click a cell → the action is inferred from the cell
// (plow / plant / harvest), queued, the avatar walks there, a progress bar plays, the result
// is applied locally and batched to POST /api/farm/actions. Free walking (keys/joystick)
// still works and cancels the queue. The world is a scaled/panned container (FarmWorld);
// the camera is untouched, so HUD/modals/toasts behave like every other scene.
import Phaser from 'phaser';
import { SCENE, GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { showToast } from '../ui/Toast.js';
import { showModal } from '../ui/Modal.js';
import { messageFor } from '../api/errors.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { NatalkaSprite } from '../ui/NatalkaSprite.js';
import { WalkController } from '../ui/WalkController.js';
import { realtime } from '../api/realtime.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { OUTDOOR_CHARACTER_SCALE } from '../ui/carCatalog.mjs';
import { ySortDepth, UI_DEPTH } from '../ui/ySort.js';
import { isInputLocked } from '../ui/inputLock.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isTouchDevice } from '../ui/isTouchDevice.js';
import { touchTransform, applyTouchTransform } from '../ui/touchEnlarge.js';
import { dir8FromVector } from '../ui/walkAnim.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { audioManager } from '../audio/AudioManager.js';
import { audioEvents } from '../audio/audioEvents.js';
import { animalSoundEvent } from '../audio/eventMap.js';
import { homeArrival } from './homeLayout.mjs';
import { FarmWorld } from '../farm/FarmWorld.js';
import { FarmHud } from '../farm/FarmHud.js';
import { openFarmMarket } from '../farm/FarmMarket.js';
import { seedUsable, pickedColor } from '../farm/farmMarket.mjs';
import { attachFarmCameraControl } from '../farm/farmCameraControl.js';
import { attachFarmBackdrop, clockInput } from '../farm/FarmBackdrop.js';
import { attachFarmCursor } from '../farm/farmCursorApply.js';
import { attachFarmPopups } from '../farm/FarmPopups.js';
import { FarmToolStrip } from '../farm/FarmToolStrip.js';
import { FarmVehicle } from '../farm/FarmVehicle.js';
import { FarmPlacer } from '../farm/FarmPlacer.js';
import { sellBack, saleNote } from '../state/resale.mjs';
import { openFarmStorage } from '../farm/FarmStorage.js';
import { objectAt, treeStage, treeTapAction, treeHint, fenceRuns } from '../farm/farmObjects.mjs';
import { animalMenuItems, animalCursor, animalTapAction } from '../farm/animalMenu.mjs';
import { nextProductAtMs } from '../farm/farmAnimals.mjs';
import { gamePanel, gameButton } from '../ui/gameUi.js';
import { menuWidth, FONT as FONT_UI } from '../ui/gameUiStyle.mjs';
import { areaCells, areaKey, areaCentre } from '../farm/footprint.mjs';
import { ACTION_FOR_TOOL, VEHICLE_DURATION_MS, areaHint, workFloat, plural, autoRefill } from '../farm/vehicles.mjs';
import { cellCentre, cellFToLocal, localToCell, inFarm, gardenExit, isExitCell, TILE_W, TILE_H } from '../farm/iso.mjs';
import { podworkoState, podworkoArrivalCell } from '../farm/podworkoPlot.mjs';
import { backyardGateArrival } from './backyardLayout.js';
import {
  stageAt, inferAction, eligibleCells, recyclableCells, tooltipFor, applyOp,
  ACTION_LABEL, ACTION_ICON, HAND_DURATION_MS, fuelNow, occupiedCells, handActionAt,
} from '../farm/farmRules.mjs';
import { findPath } from '../farm/pathfinding.mjs';
import { createQueue, enqueue, queuedKeys, currentJob, clearQueue, tick as tickQueue, jobAt, removeJob, clearWaiting, queuedAreas } from '../farm/actionQueue.mjs';
import { refreshNow } from '../farm/liveRefresh.mjs';
import { createBatcher, push as pushOp, shouldFlush, take as takeOps, pending as pendingOps } from '../farm/batcher.mjs';
import { iconText } from '../ui/iconText.js';
import { isNight } from '../farm/animalRoam.mjs';
import { cycleT } from '../farm/skyClock.mjs';
import { ambienceFor } from '../audio/eventMap.js';
import { trampleToast } from '../farm/trampleToast.mjs';
import { hutHint, hutResidents } from '../farm/hutHint.mjs';
import { attachFarmFx } from '../farm/FarmFxPeers.js';
import { progressThrottle } from '../farm/farmFx.mjs';
import { serverNow } from '../state/serverClock.mjs';

const VIEWPORT = { w: GAME_WIDTH, h: GAME_HEIGHT };
const WALK_SPEED = 270;            // px/s in local units. Still brisk — you cross the starting
                                   // farm in under three seconds — but 320 read as hurried next
                                   // to the plots she is walking between.
const VERTICAL_FACTOR = 0.6;       // WalkController scales dy by this; we pre-divide to hit targets
const ARRIVE_PX = 8;
const WALK_TIMEOUT_MS = 4000;      // a blocked walk still lets the job run (no stuck queues)
const FARM_PERSPECTIVE = { refY: 0, nearY: 1, nearScale: 1, farScale: 1 };   // flat, at outdoor scale
const SFX_FOR = { plow: 'plow', plant: 'plant', harvest: 'harvest', remove_plot: 'zasyp' };   // hand jobs (farm 6)
const REJECT_MSG = {
  FARM_TREE_NOT_READY: 'Owoce jeszcze nie dojrzały {hourglass}',
  INSUFFICIENT_MONEY_FARM: 'Za mało pieniążków {coin}',
  FARM_CROP_LOCKED: 'Ta roślina odblokuje się na wyższym poziomie {xp_star}',
  FARM_COLOR_UNKNOWN: 'Takiego koloru nie ma',
  FARM_COLOR_LOCKED: 'Ten kolor odblokujesz gwiazdką mistrzostwa {star_full}',
  FARM_LEVEL_TOO_LOW: 'Potrzebujesz wyższego poziomu {xp_star}',
  FARM_MAX_SIZE: 'Farma jest już największa',
  FARM_CELL_NOT_READY: 'Roślinka jeszcze rośnie {hourglass}',
  FARM_CELL_NOT_PLOWED: 'Najpierw zaoraj ziemię {tractor}',
  FARM_CELL_NOT_PLOWABLE: 'Tu nie da się zaorać',
  FARM_NO_FUEL: 'Brak paliwa {fuel}',
  FARM_TANK_FULL: 'Bak jest pełny {fuel}',
  FARM_TOOL_MAX: 'Masz już największą maszynę',
  FARM_KONEWKA_OWNED: 'Masz już konewkę',
  FARM_NO_CANS: 'Nie masz kanistrów {canister}',
  FARM_CELL_TAKEN: 'Tu już coś stoi',
  FARM_LANDMARK_OWNED: 'Masz już tę budowlę',
  FARM_NOT_FOR_SALE: 'Tego nie można sprzedać',
  FARM_UNKNOWN_OBJECT: 'Nie ma takiej rzeczy',
  FARM_NOT_IN_STORAGE: 'Nie masz tego w Schowku {crate}',
  FARM_OBJECT_NOT_FOUND: 'Tego już tu nie ma',
  FARM_FENCE_NOT_STRAIGHT: 'Płot stawiamy w prostej linii',
  FARM_CELL_OUT_OF_BOUNDS: 'To wystaje poza farmę',
  FARM_CELL_HAS_CROP: 'Najpierw zbierz plony {basket}',
  FARM_CELL_NOT_FALLOW: 'Tu nie ma grządki do zasypania',
  NO_PENDING_PRODUCT: 'Jeszcze nic nie ma',
  NO_FOOD: 'Brak karmy — kup ją w sklepie zoologicznym',
  ANIMAL_NOT_FOUND: 'Nie ma takiego zwierzaka',
  ANIMAL_HAPPY: 'Jest już szczęśliwy {heart}',
  ANIMAL_NOT_HUNGRY: 'Jeszcze nie jest głodne {check}',
  FARM_UNKNOWN_ANIMAL: 'Takiego zwierzaka nie ma na farmie',
};
const SEED_STORAGE_KEY = 'sd_farm_seed';

const chebyshev = (a, b) => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y));

export class FarmScene extends Phaser.Scene {
  constructor() { super(SCENE.FARM); }

  preload() {
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this.worldScale = OUTDOOR_CHARACTER_SCALE;
    this.perspective = FARM_PERSPECTIVE;
    this._seed = this._loadSeed();
    this._queue = createQueue();
    this._batch = createBatcher();
    this._jobSeq = 0;
    this._path = null;
    this._walkSince = 0;
    this._flushing = false;
    this._flushPromise = null;
    this._dead = false;
    this._exiting = false;
    this._tool = 'hand';          // or an owned vehicle: tractor | seeder | harvester

    this.world = new FarmWorld(this, { getData: () => gameState.raw, owner: window.__myId });
    const size = this.world.size;

    // Avatar inside the world container, at outdoor scale, flat.
    const spawn = this._spawnLocal(data, size);
    this.natalka = new NatalkaSprite(this, spawn.x, spawn.y, { worldScale: OUTDOOR_CHARACTER_SCALE });
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.world.objects.add(this.natalka);
    this.walker = new WalkController(this, this.natalka, {
      speed: WALK_SPEED,
      xBounds: [-1e6, 1e6], floorY: -1e6, bottomY: 1e6,          // the diamond is the real bound (blocked)
      blocked: (fx, fy) => this._walkBlocked(fx, fy),
      onBroadcast: (p) => realtime.move(p),
      autoVector: () => this._autoVector(),
    });
    fitCharacter(this);
    this._lastCell = this._avatarCell();   // an exit you arrive standing on waits until you step onto it again

    this.cam = attachFarmCameraControl(this, this.world, {
      viewport: VIEWPORT,
      getTarget: () => ({ x: this.natalka.x, y: this.natalka.feetY }),
      onTap: (cell, info) => this._onTap(cell, info),
      dragMode: () => this.placer?.mode?.kind === 'fence' || (this._tool === 'recycle' && !this.placer?.active),
      onDragStart: (cell) => (this._tool === 'recycle' && !this.placer.active ? this._recycleDrag(cell, 'start') : this.placer.onDragStart(cell)),
      onDrag: (cell) => (this._tool === 'recycle' && !this.placer.active ? this._recycleDrag(cell, 'move') : this.placer.onDrag(cell)),
      onDragEnd: (cell) => (this._tool === 'recycle' && !this.placer.active ? this._recycleDrag(cell, 'end') : this.placer.onDragEnd(cell)),
      onHover: (cell, screen) => this._onHover(cell, screen),
    });
    this.cam.snapToTarget();
    // Sky, parallax backdrop and the time-of-day light: screen-fixed layers around the world.
    this.sky = attachFarmBackdrop(this, this.world, this.cam);
    // Farm 6: the day / night ambience under the music; the 1 s tick switches it at dusk / dawn and
    // retries a clip that was still loading. Stopped when the scene goes (home, a visit, logout).
    const ambience = () => audioManager.playAmbience(ambienceFor(isNight(cycleT(clockInput()))));
    ambience();
    this._ambienceTimer = this.time.addEvent({ delay: 1000, loop: true, callback: ambience });
    this.events.once('shutdown', () => { this._ambienceTimer?.remove(); audioManager.stopAmbience(); });

    // HUD
    new MoneyBar(this);
    attachFriendsButton(this);
    this.hud = new FarmHud(this, {
      getSeed: () => this._validSeed(),
      onMarket: () => this._openMarket(),
      onExpand: () => this._expand(),
      onHome: () => this._exit('home'),
      // Everything in the Schowek — stored fence segments too — is placed one by one from storage.
      onStorage: () => { if (!isInputLocked(this)) openFarmStorage(this, { onPick: (item) => this.placer.start(item) }); },
    });
    this.strip = new FarmToolStrip(this, {
      getTool: () => this._tool,
      onPick: (id) => this._pickTool(id),
      onRefuel: (kind) => (kind === 'can' ? this._useCan() : this._refuel()),
      getSeed: () => this._validSeed(),
      getColor: () => this._seedArtColor(),
    });
    // No hover text: the cursor itself becomes the tool a click would use (farmCursor.mjs).
    this._cursor = attachFarmCursor(this);
    // Level-up and mastery pop-ups, from the server's events (never on a friend's farm).
    this._popups = attachFarmPopups(this, { onGiftsSeen: (n) => this._seenGifts(n), onHelpersSeen: (ids) => this._seenHelpers(ids) });
    this._progress = this._makeProgressBar();
    this.vehicle = new FarmVehicle(this, this.world);
    this.placer = new FarmPlacer(this, {
      apply: (op) => this._applyObjectOp(op),
      onDone: () => this._afterQueueChange(),
      toast: (reason) => showToast(this, REJECT_MSG[reason] ?? reason),
    });

    // Presence + chat (coordinates are container-local; peers on this farm share the space).
    if (window.__myId) {
      const room = `house:${window.__myId}:${this.scene.key}`;
      this._presence = attachRemotePresence(this, room);
      attachRoomChat(this, room);
      attachFarmFx(this, room, { getAnimals: () => gameState.raw?.animals ?? [] });   // visitors' watering / petting, played here
    // 4d: the server's herd for this farm — every viewer plays the same intentions.
    const herdOffs = [
      realtime.on('herd_snapshot', (m) => m.room === room && this.world.animalLayer.applyHerd(m)),
      realtime.on('herd', (m) => m.room === room && this.world.animalLayer.applyHerd(m)),
      realtime.on('herd_sound', (m) => m.room === room && this.world.animalLayer.playSound(m)),
      realtime.on('farm_changed', (m) => {                      // farm 5: a friend helped while we watch
        if (m.room !== room || !m.by || m.by === window.__myId) return;
        const trampled = m.by === 'herd' ? trampleToast(m.trampled, gameState.raw?.catalog?.farm?.crops) : null;
        if (trampled) showToast(this, trampled, 3500);           // animals on the fields (owner 2026-10-01)
        if (refreshNow({ pending: pendingOps(this._batch).length, flushing: this._flushing, job: currentJob(this._queue) })) this._reloadKeepingOurs();
        else this._refreshAfterFlush = true;                    // never over our own unsent work
      }),
    ];
    this.events.once('shutdown', () => herdOffs.forEach((off) => off?.()));
    }

    // State → visuals. Skip the 30 s refresh while local work is unsent (it would flicker back).
    this._unsub = gameState.on(() => this._onState());
    this.pollTimer = this.time.addEvent({ delay: 30000, loop: true, callback: () => {
      if (pendingOps(this._batch).length === 0 && !currentJob(this._queue) && !this._flushing) gameState.refresh().catch(() => {});
    } });
    this.localTickTimer = this.time.addEvent({ delay: 1000, loop: true, callback: () => { this.world.refresh(serverNow()); this._productDue(); } });
    this.events.once('shutdown', () => {
      this._dead = true;
      this._unsub?.();
      this.pollTimer?.remove(); this.localTickTimer?.remove();
      virtualInput.clearAction(); virtualInput.clearCancel();
      this._flushOnShutdown();   // best effort: send whatever is still pending
    });
    this._unsubOutfit = gameState.on(() => this.natalka.setOutfit(gameState.raw?.inventory?.equipped));
    this.events.once('shutdown', () => this._unsubOutfit?.());

    this.input.keyboard.on('keydown-ESC', () => {
      if (isInputLocked(this)) return;
      if (this._menu) this._closeMenu();
      else if (this.placer.active) this.placer.cancel();
      else this._cancelQueue();
    });

    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });
    if (this._seed === null) showToast(this, 'Kliknij grządkę: zaoraj, posadź, zbierz {seedling}', 3500);
    // v17 moved the Zagroda's huts and animals into the Schowek: say so once (spec 4c §2).
    if (gameState.raw?.farm?.animal_notice) {
      showModal(this, { title: 'Twoje zwierzęta czekają {chicken}', body: 'Są w Schowku. Postaw ich domki i wtedy wyjdą.', confirmLabel: 'Dobrze', cancelLabel: null })
        .then(() => this._applyObjectOp({ op: 'seen_notice' }));
    }
  }

  // A new-version reload (ui/autoUpdate.js) waits for field work still queued or unsent: it lives only here.
  reloadBlocked() {
    return pendingOps(this._batch).length > 0 || this._flushing || !!currentJob(this._queue) || this._queue.jobs.length > 0;
  }

  update(_t, dt) {
    if (!this.natalka) return;
    this.world.update(dt);
    const now = performance.now();
    // Manual input wins: it cancels the queue and re-enables the follow-cam.
    if (this.walker.manualMoving && this._tool === 'hand') {   // vehicle work runs while she walks
      if (currentJob(this._queue) || this._queue.jobs.length) this._cancelQueue();
      this.cam.setFollow(true);
    }
    this.walker.update(dt);
    if (this._menu && this.walker.manualMoving) this._closeMenu();   // walked away from it
    // Whatever moved the grid under her (a rejected expand, a size change), keep her feet on it.
    const onCell = this._avatarCell();
    if (!inFarm(onCell, this.world.size)) {
      const s = this.world.size;
      const c = cellCentre(Math.min(s - 1, Math.max(0, onCell.x)), Math.min(s - 1, Math.max(0, onCell.y)), s);
      this.natalka.x = c.x;
      this.natalka.y = c.y - (this.natalka.feetY - this.natalka.y);
      if (this.cam.follow) this.cam.snapToTarget();
    }
    fitCharacter(this);
    // Queue: walk → work → complete.
    const cur = currentJob(this._queue);
    const arrived = cur ? (this._arrivedAt(cur.anchor) || now - this._walkSince > WALK_TIMEOUT_MS) : false;
    if (arrived && this._path) { this._path = null; this.natalka.setMotion?.(0, 0); }
    for (const ev of tickQueue(this._queue, dt, { arrived, canStart: (job) => this._canStart(job) })) this._onQueueEvent(ev, now);
    // Batching
    const idle = !currentJob(this._queue) && this._queue.jobs.length === 0;
    if (!this._flushing && shouldFlush(this._batch, now, { idle })) this._flush();
    // Depth + camera + presence
    this.natalka.setDepth(this.world.animalLayer.characterDepth(this.natalka.x, this.natalka.feetY));   // beside a building: the right side of it
    if (this._presence) {
      for (const a of this._presence.avatars.values()) {
        // A peer is more than its sprite: the name tag, bike, car and chat bubble are all
        // separate scene-root objects at the same local coords — left outside the panned/
        // scaled world container they'd drift by the pan offset. Re-parent whichever exist.
        for (const obj of [a.sprite, a.label, a.bike, a.car, a._bubble?.label]) {
          if (obj && obj.parentContainer !== this.world.objects) {
            const depth = obj.depth;
            this.world.objects.add(obj);
            obj.setDepth(depth);   // keep sorting above bodies (label/bubble depths exceed any feetY)
          }
        }
        a.sprite?.setDepth(this.world.animalLayer.characterDepth(a.sprite.x, a.sprite.feetY ?? a.sprite.y));
      }
    }
    this._progress.container.setPosition(this.natalka.x, this.natalka.y - 70).setDepth(1e9);
    this.world.sortObjects();
    this.cam.update(dt);
    this.sky?.update();
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._syncTouchActions();
    this._checkCornerExit();
  }

  // ---- spawn / exits ------------------------------------------------------------------

  _spawnLocal(data, size) {
    if (Number.isFinite(data?.spawnX) && Number.isFinite(data?.spawnY)) return { x: data.spawnX, y: data.spawnY };
    if (data?.arrive === 'podworko') {                     // through Podwórko's gate: stand next to ours
      const a = podworkoArrivalCell(size);
      const c = cellCentre(a.x, a.y, size);
      return { x: c.x, y: c.y - 60 };
    }
    const cell = { x: 1, y: size - 2 };
    const c = cellCentre(cell.x, cell.y, size);
    return { x: c.x, y: c.y - 60 };   // 60 = half of the 120-px outdoor sprite: feet on the cell centre
  }

  // Off the farm, or into an object (unless she already stands in one — placed where she stood —
  // so she can always walk out).
  _walkBlocked(fx, fy) {
    const size = this.world.size;
    const to = localToCell(fx, fy, size);
    if (!inFarm(to, size)) return true;
    if (isExitCell(to, size, { podworko: !!podworkoState(gameState.raw) })) return false;   // a hut on the gate cell must not shut it
    const taken = occupiedCells(gameState.raw ?? { placements: [], catalog: {} });
    if (!taken.has(`${to.x},${to.y}`)) return false;
    const here = this._avatarCell();
    return !taken.has(`${here.x},${here.y}`);
  }

  _avatarCell() { return localToCell(this.natalka.x, this.natalka.feetY, this.world.size); }

  _checkCornerExit() {
    if (this._exiting) return;
    // Only the player's own steering leaves: a job's path across the corner or the gate does not.
    const cell = this._avatarCell();
    const exit = gardenExit(cell, this.world.size, { podworko: !!podworkoState(gameState.raw), manual: this.walker.manualMoving, from: this._lastCell });
    this._lastCell = cell;
    if (exit) this._exit(exit);
  }

  _exit(where) {
    if (this._exiting) return;
    this._exiting = true;
    this._cancelQueue();
    if (where === 'podworko') this.scene.start(SCENE.BACKYARD, backyardGateArrival());
    else this.scene.start(SCENE.HOME, homeArrival('garden'));
  }

  // ---- input --------------------------------------------------------------------------

  _onHover(cell, screen) {
    this.world.animalLayer?.setHighlight(this._menuAnimal ?? null);   // the ring: hovered (below) or menu open
    if (this.placer?.active && !isInputLocked(this)) { this.world.setHover(null); this._cursor.set(null, 'hand'); this.placer.onHover(cell); return; }
    // A modal (market, pop-up, confirm) covers the field: the field's tool must not show through.
    if (isInputLocked(this) || screen?.overHud) { this.world.setHover(null); this.world.setAreaHover(null); this._cursor.set(null); return; }
    const farm = gameState.raw?.farm;
    if (this._tool === 'recycle' && farm) {          // Zasyp: the field under the cursor goes back to grass
      if (!this._recycleFrom) this.world.setAreaHover(null);
      this.world.setHover(cell);
      this._cursor.set(cell ? 'any' : null, 'recycle');
      return;
    }
    if (this._isVehicle() && farm) {
      this.world.setHover(null);
      if (cell) {
        const a = this._areaFor(cell);
        this.world.setAreaHover(a.cells, this._hintFor(a.action, a.todo.length));
      } else this.world.setAreaHover(null);
      this._cursor.set(cell ? 'any' : null, this._tool);
      return;
    }
    const an = screen ? this.world.animalLayer.animalAtScreen(screen.x, screen.y) : null;
    if (an) { this.world.animalLayer.setHighlight(an.id); this.world.setHover(null); this._cursor.set(animalCursor(an), 'hand'); this.world.setHint(null, null); return; }
    this.world.setHover(cell);
    // Over a placed object a tap opens its menu (the open hand) — but a ripe tree is harvested
    // (the basket), and an unripe one says when its fruit comes.
    const now = serverNow();
    let obj = cell && farm ? objectAt(gameState.raw, cell) : null;
    if (!obj && screen && farm) obj = this._hutAt(screen);
    const tree = obj?.type === 'tree' ? obj : null;
    const look = tree ? (treeStage(tree, now) === 'ripe' ? 'ready' : 'object') : obj ? 'object' : null;
    this._cursor.set(cell && farm ? (look ?? stageAt(farm, cell, now)) : null, 'hand');
    // A Kurnik / Obora: who lives there and how much room is left, its animals aglow (owner 2026-10-01).
    const hut = obj && !tree ? hutHint(gameState.raw, obj) : null;
    if (hut) this.world.animalLayer.setHighlight(hutResidents(gameState.raw, obj.id));
    this.world.setHint(hut ? { x: obj.x, y: obj.y } : cell, tree ? treeHint(tree, now) : hut);   // a hut's hint sits on the hut
  }

  // A hut's roof stands over the cells behind it: the picture under the pointer finds the hut.
  _hutAt(pt) {
    const raw = gameState.raw;
    const isHut = (id) => (raw?.placements ?? []).some((p) => p.id === id && raw.catalog.farm.objects?.[p.item]?.kind === 'hut');
    const id = this.world.objectLayer.idAtScreen(pt.x, pt.y, isHut);
    return id ? (raw.placements ?? []).find((p) => p.id === id) ?? null : null;
  }

  _onTap(cell, info) {
    if (isInputLocked(this)) return;
    // An open menu: a tap anywhere outside it just closes it (owner 2026-10-01: a tap off the farm's
    // grid — the grass past the fence — left it open for good).
    if (this._menu) { this._closeMenu(); return; }
    // A tap on an animal beats the tile under it (spec 4c §2); vehicles ignore animals. Checked
    // before the "no cell" and "close the menu" exits: an animal by the fence stands half off the
    // grid, and a tap on it (or its milk / egg bubble) did nothing (owner 2026-09-30).
    const animal = this._tool === 'hand' && info && !this.placer.active ? this.world.animalLayer.animalAtScreen(info.x, info.y) : null;
    if (animal) this._closeMenu();
    else {
      if (!cell && !(this._tool === 'hand' && info && !this.placer.active && this._hutAt(info))) return;   // a hut roof off the grid still counts
      if (this._menu) { this._closeMenu(); return; }
      if (this.placer.active) { this.placer.onTap(cell, info); return; }
    }
    if (animal) {                                    // eggs / milk waiting: a tap collects (owner); else the menu
      // collect / feed / pet straight away; hungry with no food → the menu, with the way to the Rynek (owner 2026-10-01)
      const act = String(animal.id).startsWith('local-') ? 'menu' : animalTapAction(animal, gameState.raw);
      if (act === 'collect') this._animalOp({ op: 'collect_animal', id: animal.id });
      else if (act === 'feed') this._animalOp({ op: 'feed_animal', id: animal.id });
      else if (act === 'pet') this._animalOp({ op: 'pet_animal', id: animal.id });
      else if (act === 'happy') this.world.animalLayer.hearts(animal.id, 2);     // already happy: a little love back
      else this._openAnimalMenu(animal);
      return;
    }
    let obj = this._tool === 'hand' && cell ? objectAt(gameState.raw, cell) : null;   // vehicles ignore objects
    if (!obj && this._tool === 'hand' && info) obj = this._hutAt(info);         // a tap on its roof
    if (obj?.type === 'tree' && treeTapAction(obj, serverNow()) === 'harvest') { this._harvestTree(obj); return; }
    if (obj) { this._openObjectMenu(obj); return; }
    if (this._tool === 'recycle') { this._recycle([cell]); return; }
    if (this._isVehicle()) { this._tapArea(cell, info?.wasTouch); return; }
    this._enqueueCell(cell);
  }

  // ---- vehicles ------------------------------------------------------------------------

  _isVehicle(tool = this._tool) { return tool !== 'hand' && tool !== 'recycle'; }

  _toolSizeOf(id) { return gameState.raw?.farm?.tools?.[id] ?? 0; }

  _pickTool(id) {                          // a tap on the strip
    if (isInputLocked(this)) return;
    if (this._isVehicle(id) && !this._toolSizeOf(id)) { this._openMarket(null, 'narzedzia'); return; }
    this._setTool(id);
  }

  _setTool(id) {                           // also called while the market holds the input lock
    if (id === this._tool) return;
    this._closeMenu();                     // a Kurnik menu left open under a new tool confused phones
    this._tool = id;
    clearWaiting(this._queue);             // the area being worked still finishes
    this.strip.setPaused(false);
    this.world.setAreaHover(null);
    this.world.setHover(null);
    this._afterQueueChange();
    this.strip.refresh();
    this._cursor.set(null, id);
  }

  _areaFor(cell) {
    const raw = gameState.raw;
    const action = ACTION_FOR_TOOL[this._tool];
    const cells = areaCells(cell, this._toolSizeOf(this._tool), raw.farm.size);
    const todo = eligibleCells(raw.farm, cells, action, serverNow());
    return { action, cells, todo };
  }

  _hintFor(action, n) {
    const fc = gameState.raw.catalog.farm;
    const seed = this._validSeed();
    return areaHint(action, n, action === 'plow' ? { price: fc.plow_cost }
      : action === 'plant' ? { seedName: seed ? fc.crops[seed].name_pl : '?', price: seed ? fc.crops[seed].seed_price : 0 } : {});
  }

  _tapArea(cell, wasTouch) {
    const waiting = jobAt(this._queue, cell);
    if (waiting) { removeJob(this._queue, waiting); this._afterQueueChange(); return; }
    const { action, cells, todo } = this._areaFor(cell);
    const cur = currentJob(this._queue);
    if (cur?.tool && areaKey(cur.area) === areaKey(cells)) return;
    if (!todo.length) { showToast(this, 'Tu nie ma nic do zrobienia', 1200); return; }
    if (action === 'plant' && !this._validSeed()) { this._openMarket(); return; }
    // One tap starts it, on phones too (owner 2026-10-11: two taps on one spot were tricky);
    // a wrong tap is undone by tapping the waiting area again.
    this.world.setAreaHover(wasTouch ? null : cells, null);
    enqueue(this._queue, {
      id: ++this._jobSeq, action, tool: this._tool, walk: false, cells, area: cells, anchor: cell,
      seed: action === 'plant' ? this._validSeed() : null, color: action === 'plant' ? this._validColor(this._validSeed()) : null,
      durationMs: VEHICLE_DURATION_MS,
    });
    audioManager.playSfx('click');
    this._afterQueueChange();
  }

  _afterQueueChange() {
    this.world.setQueuedAreas(queuedAreas(this._queue));
    this.world.setQueued(queuedKeys(this._queue));
    if (this.strip?.paused && !this._queue.blocked) this.strip.setPaused(false);   // no refuel button for nothing
  }

  _canStart(job) {
    if (!job.tool) return true;
    const raw = gameState.raw;
    const todo = eligibleCells(raw.farm, job.area, job.action, serverNow()).length;
    return todo === 0 || fuelNow(raw.farm, raw.catalog.farm.fuel, serverNow()) >= todo;
  }

  _refuel() {
    const op = { op: 'refuel', cells: [], seed: null, tool: null };
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return false; }
    pushOp(this._batch, op, performance.now());
    this.strip.setPaused(false);
    audioEvents.emit('buy');             // a refill is a purchase, not an unlock: the coin sound
    gameState.emit();
    return true;
  }

  // ---- objects (sub-project 4a) --------------------------------------------------------

  _applyObjectOp(op) {
    const full = { cells: [], seed: null, tool: null, ...op };
    const res = gameState.applyFarmLocal(full);
    if (!res.ok) return res;
    pushOp(this._batch, full, performance.now());
    audioEvents.emit(op.op === 'sell_object' ? 'sell' : op.op === 'buy_object' ? 'buy' : 'place');
    gameState.emit();
    // Placing flushes at once too: the object gets its real id from the reply, and until then
    // it can't be moved or sold (a 'local-N' id means nothing to the server).
    if (['buy_object', 'buy_animal', 'sell_object', 'place', 'place_fence', 'seen_notice'].includes(op.op)) this._flush();
    return res;
  }

  // A ripe tree tapped with the hand (4b): pay its fruit locally, float the coins, send the op.
  _harvestTree(p) {
    if (String(p.id).startsWith('local-')) { showToast(this, 'Chwileczkę… {hourglass}', 900); return; }   // not confirmed by the server yet
    const op = { op: 'harvest_tree', id: p.id };
    const xp0 = gameState.raw.xp;
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return; }
    this._floatAt(cellCentre(p.x, p.y, this.world.size), workFloat({ money: res.coins, xp: gameState.raw.xp - xp0 }));
    audioEvents.emit('sell');
    pushOp(this._batch, op, performance.now());
    gameState.emit();
    this._flush();
  }

  _openObjectMenu(p) {
    this._closeMenu();
    if (String(p.id).startsWith('local-')) { showToast(this, 'Chwileczkę… {hourglass}', 900); return; }   // not confirmed by the server yet
    const fc = gameState.raw.catalog.farm;
    const spec = p.item.startsWith('plaque:') ? fc.objects.plaque : fc.objects[p.item];
    let name = p.item.startsWith('plaque:') ? `${spec.name_pl}: ${fc.crops[p.item.slice(7)]?.name_pl ?? ''}` : spec.name_pl;
    if (spec.kind === 'hut') {                       // phones have no hover: its count in the title, its animals aglow
      const who = hutResidents(gameState.raw, p.id);
      name = `${name} · ${who.length}/${spec.holds}`;
      this._menuAnimal = who;
      this.world.animalLayer.setHighlight(who);
    }
    const c = cellCentre(p.x, p.y, this.world.size);
    const items = [
      ['Przesuń', () => this.placer.startMove(p)],
      ['Odwróć', () => this._applyObjectOp({ op: 'flip', id: p.id })],
      ['Schowaj', () => this._applyObjectOp({ op: 'pick_up', id: p.id })],
    ];
    if (spec.kind !== 'plaque') {
      const sale = sellBack(gameState.raw.farm?.old_price_objects, fc.legacy_object_prices, p.item, spec.price);
      items.push([`Sprzedaj ${sale.coins.toLocaleString('pl-PL')} {coin}`, () => this._sellObject(p, name, spec, sale)]);
    }
    this._menuPanel(name, items, this.world.container.getWorldTransformMatrix().transformPoint(c.x, c.y));
  }

  // A small wooden menu above a point on screen: a title and one button per [label, fn].
  _menuPanel(name, items, at) {
    // Sized to its longest line (a Złota tabliczka's name is long), in the game style.
    const probe = iconText(this, 0, 0, '', { fontFamily: FONT_UI, fontSize: '18px', fontStyle: 'bold' });
    const W = menuWidth([name, ...items.map(([l]) => l)], (l) => probe.setText(l).width);
    probe.destroy();
    const BH = 44, GAP = 10, H = 70 + items.length * (BH + GAP);
    // Screen space, above the object: inside the world it was dimmed by the time-of-day light
    // and scaled with the zoom.
    const box = this.add.container(Math.max(W / 2 + 10, Math.min(GAME_WIDTH - W / 2 - 10, at.x)), Math.max(H / 2 + 10, at.y - 50 - H / 2))
      .setDepth(UI_DEPTH + 50).setScrollFactor(0);
    // Its own invisible hit area: a tap inside the panel (between the buttons) is the menu's, not 'away'.
    box.add(this.add.rectangle(0, 0, W, H, 0xffffff, 0.001).setInteractive());
    box.add(gamePanel(this, 0, 0, W, H));
    box.add(iconText(this, 0, -H / 2 + 30, name, { fontFamily: FONT_UI, fontSize: '18px', color: '#5a3414', fontStyle: 'bold' }).setOrigin(0.5));
    items.forEach(([label, fn], i) => {
      const y = -H / 2 + 72 + i * (BH + GAP);
      const variant = label.startsWith('Sprzedaj') ? 'danger' : 'secondary';
      box.add(gameButton(this, 0, y, W - 36, BH, label, () => { this._closeMenu(); fn(); }, { variant, fontSize: 17 }));
    });
    applyTouchTransform(box, touchTransform(this, { x: box.x, y: box.y, w: W, h: H }, { fixed: true }).t);   // finger-sized
    this._menu = box;
  }

  // A farm animal tapped with the hand (4c): Zbierz / Nakarm / Pogłaskaj (animalMenu.mjs).
  _openAnimalMenu(a) {
    this._closeMenu();
    if (String(a.id).startsWith('local-')) { showToast(this, 'Chwileczkę… {hourglass}', 900); return; }   // not confirmed by the server yet
    const name = gameState.raw.catalog.farm.animals[a.type]?.name_pl ?? a.type;
    const items = animalMenuItems(a, gameState.raw).map(({ op, label, enabled, reason }) =>
      [label, enabled === false ? () => showToast(this, REJECT_MSG[reason] ?? REJECT_MSG.ANIMAL_HAPPY, 1300)
        : op === 'buy_food' ? () => this._openMarket(null, 'zwierzeta')            // no food yet: the Rynek's Zwierzęta
          : () => this._animalOp({ op, id: a.id })]);
    const cell = this.world.animalLayer.cellOf(a.id) ?? { x: 0, y: 0 };
    const c = cellFToLocal(cell.x + 0.5, cell.y + 0.5, this.world.size);
    this._menuAnimal = a.id;                                  // ringed while its menu is open (phones have no hover)
    this.world.animalLayer.setHighlight(a.id);
    this._menuPanel(name, items, this.world.container.getWorldTransformMatrix().transformPoint(c.x, c.y - 30));
  }

  _animalOp(op) {
    const xp0 = gameState.raw.xp;
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return; }
    if (op.op === 'collect_animal') {
      const cell = this.world.animalLayer.cellOf(op.id) ?? { x: 0, y: 0 };
      this._floatAt(cellFToLocal(cell.x + 0.5, cell.y + 0.5, this.world.size), [`+${res.count} ${res.product === 'mleko' ? '{milk}' : '{egg}'}`, workFloat({ xp: gameState.raw.xp - xp0 })].filter(Boolean).join('  '));
    }
    const a = (gameState.raw.animals ?? []).find((x) => x.id === op.id);
    if (op.op === 'pet_animal') this.world.animalLayer.hearts(op.id);
    audioEvents.emit(animalSoundEvent(op.op, a?.type));
    { const cell = this.world.animalLayer.cellOf(op.id) ?? { x: 0, y: 0 }, c = cellFToLocal(cell.x + 0.5, cell.y + 0.5, this.world.size);
      this._fxSend({ kind: { pet_animal: 'pet', feed_animal: 'feed', collect_animal: 'collect' }[op.op], animal: op.id, x: c.x, y: c.y }); }   // farm 6: munching / pecking / a happy sound, not the moo
    pushOp(this._batch, op, performance.now());
    gameState.emit();
    this._flush();
  }

  // Zasyp (owner 2026-09-29): empty fields back to grass — free, a crop is protected. Since
  // 2026-09-30 (owner) a hand job like plowing: she walks up to each field and the progress bar
  // runs before it turns to grass (the queue's completion sends the remove_plot op).
  _recycle(cells) {
    const raw = gameState.raw;
    if (!raw?.farm || !cells.length) return;
    const now = serverNow();
    const todo = recyclableCells(raw.farm, cells, now);
    if (!todo.length) {
      const crop = cells.some((c) => ['planted', 'ready'].includes(stageAt(raw.farm, c, now)));
      if (crop) showToast(this, REJECT_MSG.FARM_CELL_HAS_CROP, 1400);
      return;
    }
    const queued = queuedKeys(this._queue);
    for (const c of todo) {
      if (queued.has(`${c.x},${c.y}`)) continue;                     // already on its way
      enqueue(this._queue, { id: ++this._jobSeq, action: 'remove_plot', cells: [c], anchor: c, seed: null, tool: null, durationMs: HAND_DURATION_MS });
    }
    audioManager.playSfx('click');
    this.world.setQueued(queuedKeys(this._queue));
  }

  // Press-drag a straight line of fields (like a fence run); release recycles its empty ones.
  _recycleDrag(cell, phase) {
    if (phase === 'start') this._recycleFrom = cell;
    if (!this._recycleFrom || !cell) return;
    const line = fenceRuns(this._recycleFrom, cell).flat();
    const todo = recyclableCells(gameState.raw.farm, line, serverNow());
    if (phase !== 'end') {
      this.world.setAreaHover(line, `Zasypie ${todo.length} ${plural(todo.length, 'pole', 'pola', 'pól')}`);
      return;
    }
    this._recycleFrom = null;
    this.world.setAreaHover(null);
    this._recycle(line);
  }

  // Buying drops straight into place mode; ESC leaves it in the Schowek. A Płot is paid per
  // segment when a run is placed, so its card opens fence drawing without buying anything.
  _buyObject(item) {
    if (item === 'plot') { this.placer.startFence(); return; }
    const r = this._applyObjectOp({ op: 'buy_object', item, qty: 1 });
    if (!r.ok) { showToast(this, REJECT_MSG[r.reason] ?? 'Nie udało się'); return; }
    this.placer.start(item);
  }

  _closeMenu() {
    this._menu?.destroy(); this._menu = null;
    if (this._menuAnimal != null) { this._menuAnimal = null; this.world.animalLayer?.setHighlight(null); }
  }

  _sellObject(p, name, spec, sale) {
    const go = () => { const r = this._applyObjectOp({ op: 'sell_object', id: p.id }); if (!r.ok) showToast(this, REJECT_MSG[r.reason] ?? 'Nie udało się'); };
    // Quick-sell cheap things on the desktop; a phone always asks — 'Sprzedaj' sits a finger's
    // width under 'Schowaj' there.
    if (spec.price < 1000 && !isTouchDevice()) { go(); return; }
    showModal(this, { title: `Sprzedać ${name} za ${sale.coins.toLocaleString('pl-PL')} {coin}?`, body: saleNote(sale), confirmLabel: 'Sprzedaj' }).then((ok) => { if (ok) go(); });
  }

  _useCan() {
    const op = { op: 'use_can', cells: [], seed: null, tool: null };
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return false; }
    pushOp(this._batch, op, performance.now());
    this.strip.setPaused(false);
    audioEvents.emit('buy');
    gameState.emit();
    return true;
  }

  // Reload the state (a friend changed our farm) and re-apply whatever we queued meanwhile on top.
  _reloadKeepingOurs() {
    gameState.refresh().then(() => {
      if (this._dead) return;
      const later = pendingOps(this._batch);
      if (later.length) { for (const op of later) applyOp(gameState.raw, op, serverNow()); gameState.emit(); }
    }).catch(() => {});
  }

  // Food (owner 2026-10-01): a plain server buy, not a farm op — so the farm's own unsent work
  // goes first and is replayed onto the state the buy returns (as _reloadKeepingOurs does).
  async _buyFood(food) {
    try {
      await this._flush();
      await this._flushPromise?.catch(() => {});
      await gameState.buyFood(food);
      if (this._dead) return false;
      const later = pendingOps(this._batch);
      if (later.length) { for (const op of later) applyOp(gameState.raw, op, serverNow()); gameState.emit(); }
      audioEvents.emit('buy');
      return true;
    } catch (e) {
      if (!this._dead) showToast(this, messageFor(e));
      return false;
    }
  }

  _seenHelpers(ids) {                      // farm 5: the helpers pop-up showed these
    const op = { op: 'seen_helpers', cells: [], seed: null, tool: null, ids };
    if (gameState.applyFarmLocal(op).ok) { pushOp(this._batch, op, performance.now()); gameState.emit(); }
  }

  _seenGifts(count) {
    const op = { op: 'seen_gifts', cells: [], seed: null, tool: null, count };
    if (gameState.applyFarmLocal(op).ok) { pushOp(this._batch, op, performance.now()); gameState.emit(); }
  }

  _buyTool(tool) {
    const op = { op: 'buy_tool', cells: [], seed: null, tool };
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return false; }
    pushOp(this._batch, op, performance.now());
    audioEvents.emit('upgrade');
    gameState.emit();
    this._flush();
    this._setTool(tool);                  // a new vehicle is ready in the strip
    return true;
  }

  // The Konewka (owner 2026-10-27): used on friends' farms only, so it never joins this farm's tool strip.
  _buyKonewka() {
    const op = { op: 'buy_konewka' };
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return false; }
    pushOp(this._batch, op, performance.now());
    audioEvents.emit('upgrade');
    gameState.emit();
    this._flush();
    return true;
  }

  _enqueueCell(cell) {
    // Already the worker's active job: a repeat tap would queue a duplicate that later
    // rejects (the cell got consumed by the job already in flight) with a bogus toast.
    const cur = currentJob(this._queue);
    if (cur && cur.anchor.x === cell.x && cur.anchor.y === cell.y) return;
    const raw = gameState.raw;
    if (!raw?.farm) return;
    const seed = this._validSeed();
    const nowMs = serverNow();
    const stage = stageAt(raw.farm, cell, nowMs);
    const action = inferAction(stage, { seed });
    if (action === 'pick_seed') { this._openMarket(cell); return; }
    if (!action) { showToast(this, tooltipFor(raw.farm.cells[`${cell.x},${cell.y}`], stage, raw.catalog.farm.crops, nowMs), 1500); return; }
    const fc = raw.catalog.farm;
    const cost = action === 'plow' ? fc.plow_cost : action === 'plant' ? fc.crops[seed].seed_price : 0;
    if (raw.money < cost) { showToast(this, REJECT_MSG.INSUFFICIENT_MONEY_FARM); return; }
    const cells = eligibleCells(raw.farm, [cell], action, nowMs);
    if (!cells.length) return;
    enqueue(this._queue, {
      id: ++this._jobSeq, action, cells, anchor: cell,
      seed: action === 'plant' ? seed : null, color: action === 'plant' ? this._validColor(seed) : null, tool: null, durationMs: HAND_DURATION_MS,
    });
    audioManager.playSfx('click');
    this.world.setQueued(queuedKeys(this._queue));
  }

  _cancelQueue() {
    if (currentJob(this._queue)?.tool) clearWaiting(this._queue);   // the working area finishes
    else {
      clearQueue(this._queue);
      this._path = null;
      this._progress.container.setVisible(false);
    }
    this.strip?.setPaused(false);
    this._afterQueueChange();
  }

  _syncTouchActions() {
    if (isInputLocked(this)) { virtualInput.clearAction(); virtualInput.clearCancel(); return; }
    const raw = gameState.raw;
    const cell = this._avatarCell();
    const seed = this._validSeed();
    const action = this._tool === 'hand' ? handActionAt(raw, cell, serverNow(), { seed }) : null;
    const label = action === 'pick_seed' ? 'Nasiona' : (action ? ACTION_LABEL[action] : null);
    const cur = virtualInput.getAction();
    if (!label) virtualInput.clearAction();
    else if (cur?.label !== label) virtualInput.registerAction(label, () => this._enqueueCell(this._avatarCell()));
    const busy = currentJob(this._queue) || this._queue.jobs.length > 0;
    if (busy && !virtualInput.getCancel()) virtualInput.registerCancel(() => this._cancelQueue());
    if (!busy && virtualInput.getCancel()) virtualInput.clearCancel();
  }

  // ---- queue execution ----------------------------------------------------------------

  _arrivedAt(anchor) {
    return chebyshev(this._avatarCell(), anchor) <= 1;
  }

  _autoVector() {
    if (!this._path?.length) return null;
    const size = this.world.size;
    const next = this._path[0];
    const target = cellCentre(next.x, next.y, size);
    const dx = target.x - this.natalka.x, dy = target.y - this.natalka.feetY;
    if (Math.hypot(dx, dy) < ARRIVE_PX) { this._path.shift(); return this._path.length ? this._autoVector() : null; }
    return { dx, dy: dy / VERTICAL_FACTOR };
  }

  _onQueueEvent(ev, now) {
    const job = ev.job;
    if (job.tool) { this._onVehicleEvent(ev, now); return; }
    if (ev.type === 'walk') {
      this._walkSince = now;
      // She walks to the tapped plot, the view stays put (owner 2026-10-27: "I don't like that the camera is
      // moving also"); walking with the keys / joystick brings the follow back (update()).
      this.cam.setFollow(false);
      if (this._arrivedAt(job.anchor)) { this._path = []; return; }
      const from = this._avatarCell();
      const size = this.world.size;
      const taken = occupiedCells(gameState.raw);                       // walk around objects
      this._path = findPath(from, job.anchor, size, (x, y) => taken.has(`${x},${y}`), { allowBlockedGoal: true }) ?? [];
      // Stop one cell short: the worker stands next to the plot, not on it.
      if (this._path.length > 1) this._path.pop();
      return;
    }
    if (ev.type === 'start') {
      const c = cellCentre(job.anchor.x, job.anchor.y, this.world.size);
      this.natalka.setFacing8?.(dir8FromVector(c.x - this.natalka.x, c.y - this.natalka.feetY));
      this._fxSend({ kind: 'job_start', action: job.action, tool: job.action === 'remove_plot' ? 'recycle' : 'hand', x: c.x, y: c.y });
      this._progress.icon.setText(ACTION_ICON[job.action]);
      this._progress.fill.width = 0;
      this._progress.container.setVisible(true);
      return;
    }
    if (ev.type === 'progress') { this._progress.fill.width = 60 * ev.p; this._fxProgress(ev.p, job.action === 'remove_plot' ? 'recycle' : 'hand'); return; }
    // complete
    this._progress.container.setVisible(false);
    const before = { money: gameState.raw.money, xp: gameState.raw.xp };
    const op = { op: job.action, cells: job.cells.map((c) => [c.x, c.y]), seed: job.seed, color: job.color ?? null, tool: null };
    const res = gameState.applyFarmLocal(op);
    if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); this.world.setQueued(queuedKeys(this._queue)); return; }
    pushOp(this._batch, op, now);
    this._fx(job, { money: gameState.raw.money - before.money, xp: gameState.raw.xp - before.xp });
    audioEvents.emit(SFX_FOR[job.action]);
    { const c = cellCentre(job.anchor.x, job.anchor.y, this.world.size);
      this._fxSend({ kind: 'job_end', action: job.action, tool: job.action === 'remove_plot' ? 'recycle' : 'hand', x: c.x, y: c.y,
        money: gameState.raw.money - before.money, xp: gameState.raw.xp - before.xp }); }
    gameState.emit();
    this.world.setQueued(queuedKeys(this._queue));
  }

  _onVehicleEvent(ev, now) {
    const job = ev.job;
    if (ev.type === 'blocked') {
      // The Karnister fills itself (owner 2026-10-16). The queue stays blocked until its next tick
      // re-checks the fuel, and only a new block fires this again, so a refill can't loop.
      const raw = gameState.raw;
      const auto = autoRefill({ cans: raw.farm.fuel_cans ?? 0, money: raw.money, cfg: raw.catalog.farm.fuel });
      if (auto && (auto.kind === 'can' ? this._useCan() : this._refuel())) { showToast(this, auto.toast, 1800); return; }
      this.strip.setPaused(true);
      return;
    }
    if (ev.type === 'start') {
      this.strip.setPaused(false);
      this.vehicle.popIn(job.tool, gameState.raw.farm.tools[job.tool], areaCentre(job.area, this.world.size), job.action);
      { const c = areaCentre(job.area, this.world.size);
        this._fxSend({ kind: 'job_start', action: job.action, tool: job.tool, size: gameState.raw.farm.tools[job.tool], x: c.x, y: c.y }); }
      audioEvents.emit(`vehicle_${job.tool}`);        // farm 6: the machine works now (not after it left — review)
      this._afterQueueChange();
      return;
    }
    if (ev.type === 'progress') { this.vehicle.setProgress(ev.p); this._fxProgress(ev.p, job.tool); return; }
    // complete
    this.vehicle.popOut();
    const raw = gameState.raw;
    const before = { money: raw.money, xp: raw.xp, fuel: fuelNow(raw.farm, raw.catalog.farm.fuel, serverNow()) };
    const op = { op: job.action, cells: job.area.map((c) => [c.x, c.y]), seed: job.seed, color: job.color ?? null, tool: job.tool };
    const res = gameState.applyFarmLocal(op);
    this._afterQueueChange();
    const vc = areaCentre(job.area, this.world.size);
    this._fxSend({ kind: 'job_end', action: job.action, tool: job.tool, x: vc.x, y: vc.y,
      ...(res.ok ? { money: raw.money - before.money, xp: raw.xp - before.xp, fuel: -Math.round(before.fuel - raw.farm.fuel.units) } : {}) });
    if (!res.ok) {
      if (res.reason !== 'FARM_NO_CELLS') showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się');   // an overlap already did it
      return;
    }
    pushOp(this._batch, op, now);
    this._floatAt(areaCentre(job.area, this.world.size), workFloat({
      money: raw.money - before.money, fuel: -Math.round(before.fuel - raw.farm.fuel.units), xp: raw.xp - before.xp,
    }));
    gameState.emit();
  }

  // farm activity everyone on this farm sees (farm/FarmFxPeers.js plays it on their screens)
  _fxSend(p) { if (window.__myId) realtime.farmFx(p); }
  _fxProgress(p, tool) {
    this._fxThrottle ??= progressThrottle(250);
    if (this._fxThrottle(p, performance.now())) this._fxSend({ kind: 'job_progress', tool, p });
  }

  _fx(job, gained = { money: 0, xp: 0 }) {
    const fc = gameState.raw.catalog.farm;
    const n = job.cells.length;
    let label;
    if (job.action === 'plow') label = `-${n * fc.plow_cost} {coin}  +${n * fc.plow_xp} XP`;
    else if (job.action === 'plant') label = `-${n * fc.crops[job.seed].seed_price} {coin}  +${n * fc.crops[job.seed].xp} XP`;
    else if (job.action === 'remove_plot') return;                     // Zasyp is free: nothing to float
    else label = workFloat(gained);       // the ACTUAL money + XP (legacy price, mastery, harvest XP)
    this._floatAt(cellCentre(job.anchor.x, job.anchor.y, this.world.size), label);
  }

  _floatAt(c, label) {
    const t = iconText(this, c.x, c.y - TILE_H, label, { fontFamily: 'system-ui', fontSize: '18px', color: '#ffe066', fontStyle: 'bold', stroke: '#000', strokeThickness: 3 })
      .setOrigin(0.5).setDepth(1e9);
    this.world.objects.add(t);
    // Held readable while it drifts up, then faded (owner 2026-10-27: at 0.9 s, fading from the start, the
    // harvest reward could not be read).
    this.tweens.chain({ targets: t, tweens: [
      { y: t.y - 24, duration: 1500, ease: 'Sine.Out' },
      { y: t.y - 44, alpha: 0, duration: 600, ease: 'Quad.In', onComplete: () => t.destroy() },
    ] });
  }

  _makeProgressBar() {
    const container = this.add.container(0, 0).setVisible(false);
    const bg = this.add.rectangle(0, 0, 64, 12, 0x000000, 0.7).setStrokeStyle(1, 0xffffff, 0.6);
    const fill = this.add.rectangle(-30, 0, 0, 8, 0x7ed957, 1).setOrigin(0, 0.5);
    const icon = iconText(this, 0, -22, '', { fontSize: '22px' }).setOrigin(0.5);
    container.add([bg, fill, icon]);
    this.world.objects.add(container);
    return { container, fill, icon };
  }

  // Ustawienia → Wyloguj (ui/leaveSafely.mjs): stop the work queue and wait until the last
  // batch reached the server, so nothing is sent into the new game or after the session is gone.
  async settleForLeave() {
    clearQueue(this._queue);
    this._path = null;
    this._progress.container.setVisible(false);
    await this._flushPromise?.catch(() => {});
    await this._flush();
    await this._flushPromise?.catch(() => {});
  }

  async _flush() {
    if (this._flushing) return;            // the in-flight response's replay picks the rest up
    const ops = takeOps(this._batch);
    if (!ops.length) return;
    this._flushing = true;
    const promise = gameState.farmActions(ops);
    this._flushPromise = promise;
    try {
      const out = await promise;
      if (this._dead) return;    // scene torn down mid-flight: skip toast/replay on dead objects
      for (const r of out?.rejected ?? []) showToast(this, r.message ?? 'Nie udało się');
      // The server refused an expand the client already applied: its grid is the old one, so undo
      // the avatar slide and drop what was done since on the grown grid (it would land d rows off).
      if (this._expandSlide && (out?.rejected ?? []).some((r) => ops[r.index]?.op === 'expand')) {
        this.natalka.y -= this._expandSlide;
        takeOps(this._batch);
        this._cancelQueue();
        if (this.cam.follow) this.cam.snapToTarget();
        this._expandSlide = 0;
        gameState.refresh().catch(() => {});
        return;
      }
      if (ops.some((o) => o.op === 'expand')) this._expandSlide = 0;
      this._popups.show(out?.events);
      // Ops queued while the request was in flight were applied to the OLD raw; replay them.
      const later = pendingOps(this._batch);
      if (later.length) { for (const op of later) applyOp(gameState.raw, op, serverNow()); gameState.emit(); }
    } catch (e) {
      if (this._dead) return;
      showToast(this, messageFor(e));
      gameState.refresh().catch(() => {});
    } finally {
      this._flushing = false;
      this._flushPromise = null;
      // A friend helped while this batch was on its way (farm 5): now that nothing of ours is unsent, reload.
      if (this._refreshAfterFlush && !this._dead && pendingOps(this._batch).length === 0) {
        this._refreshAfterFlush = false;
        this._reloadKeepingOurs();
      }
    }
  }

  // Shutdown: `_flush()` bails out early if a request is already in flight, which would
  // otherwise silently drop any ops still pending. Chain onto that in-flight promise (it
  // settles either way) and send whatever remains, best-effort — nothing here touches scene
  // objects, so it's safe to run after teardown starts.
  // An egg or milk is due (farmAnimals.nextProductAtMs): fetch it now instead of on the 30 s poll,
  // once per due time and only while nothing local is unsent (the same rule as the poll).
  _productDue() {
    const at = nextProductAtMs(gameState.raw?.animals);
    if (at === null || serverNow() < at + 1000 || this._productFetchedFor === at) return;
    if (pendingOps(this._batch).length || currentJob(this._queue) || this._flushing) return;
    this._productFetchedFor = at;
    gameState.refresh().catch(() => {});
  }

  _flushOnShutdown() {
    const send = () => {
      const ops = takeOps(this._batch);
      if (ops.length) gameState.farmActions(ops).catch(() => {});
    };
    if (this._flushing && this._flushPromise) this._flushPromise.then(send, send);
    else send();
  }

  // ---- state → visuals ----------------------------------------------------------------

  _onState() {
    this.world.refresh(serverNow());
    // Level-ups are announced by the pop-ups (FarmPopups.js), from the server's events.
  }

  // ---- market / expand ----------------------------------------------------------------

  _loadSeed() { try { return localStorage.getItem(SEED_STORAGE_KEY); } catch { return null; } }
  _saveSeed(id) { try { localStorage.setItem(SEED_STORAGE_KEY, id); } catch { /* private mode */ } }

  // A sticky seed from localStorage may name a crop the current catalog no longer has
  // (removed, or a stale value from another save), or one now above the player's level (a crop
  // moved later on the Kwiaty ladder, 2026-10-20) — guard every read against the catalog
  // instead of trusting `this._seed` directly, and forget a seed that no longer resolves.
  _validSeed() {
    if (seedUsable(gameState.raw?.catalog?.farm, this._seed, gameState.raw?.level)) return this._seed;
    if (this._seed !== null) {
      this._seed = null;
      try { localStorage.removeItem(SEED_STORAGE_KEY); } catch { /* private mode */ }
    }
    return null;
  }

  // The colour planted with this seed (Kwiaty §7): the remembered one if its stars are there, else the
  // start colour; null for a crop without colours.
  _validColor(seed) {
    const raw = gameState.raw ?? {};
    if (!seed) return null;
    let storage = null;
    try { storage = window.localStorage; } catch { /* pickedColor keeps a session copy */ }
    return pickedColor(raw.catalog?.farm, seed, raw.farm?.mastery?.[seed]?.stars ?? 0, storage);
  }

  // The picked seed's colour as a sprite family suffix: null for the start colour (the plain sprites).
  _seedArtColor() {
    const seed = this._validSeed(), color = this._validColor(seed);
    return color && color !== gameState.raw?.catalog?.farm?.crops?.[seed]?.colors?.[0] ? color : null;
  }

  // `plantCell` is set when the market was opened by tapping a plowed tile: picking a seed
  // there is the same gesture as planting it, so the tap that opened the market plants.
  _openMarket(plantCell = null, tab = 'wszystko') {
    if (isInputLocked(this)) return;
    openFarmMarket(this, { selected: this._validSeed(), tab, onBuyTool: (t) => this._buyTool(t), onBuyKonewka: () => this._buyKonewka(), onRefuel: () => this._refuel(), onUseCan: () => this._useCan(), onBuyObject: (item) => this._buyObject(item),
      onBuyAnimal: (sp) => { const r = this._applyObjectOp({ op: 'buy_animal', item: sp }); if (!r.ok) showToast(this, REJECT_MSG[r.reason] ?? 'Nie udało się'); return r.ok; },
      onBuyFood: (food) => this._buyFood(food) }).then((seed) => {
      if (!seed) return;
      this._seed = seed;
      this._saveSeed(seed);
      this.hud.refresh();
      this.strip.refresh();
      if (plantCell) this._enqueueCell(plantCell);
    });
  }

  _expand() {
    if (isInputLocked(this)) return;
    const nx = gameState.raw?.farm?.next_expansion;
    if (!nx) return;
    showModal(this, {
      title: 'Powiększyć farmę?',
      body: `${nx.size}×${nx.size} za ${nx.cost} {coin} (od poziomu ${nx.min_level})`,
      confirmLabel: 'Powiększ',
    }).then((ok) => {
      if (!ok) return;
      const op = { op: 'expand', cells: [], seed: null, tool: null };
      // Growing to the right renumbers every farm y: queued / in-flight jobs hold the old cells
      // and would work plots d rows away (final review). Drop them; the child taps again.
      this._cancelQueue();
      const wasSize = gameState.raw.farm.size;
      const res = gameState.applyFarmLocal(op);
      if (!res.ok) { showToast(this, REJECT_MSG[res.reason] ?? 'Nie udało się'); return; }
      // The farm grows to the right: every farm y shifts by the growth (farmRules growRight), which
      // in the container's local space keeps each cell's x and moves it down by d half-tiles.
      // Slide the avatar with the ground (and re-snap the follow-cam), so nothing moves on screen
      // and the new grass appears on the right-bottom and right-top edges.
      this._expandSlide = (gameState.raw.farm.size - wasSize) * (TILE_H / 2);
      this.natalka.y += this._expandSlide;
      if (this.cam.follow) this.cam.snapToTarget();
      pushOp(this._batch, op, performance.now());
      audioEvents.emit('upgrade');
      gameState.emit();
      this._flush();
    });
  }
}
